import React from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import HomeIcon from '@mui/icons-material/Home';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { theme as appTheme } from '../../theme';
import { AppBarPadding, BottomPadding } from '../../utils/helperUtils';
import { useAuth } from '../../context/AuthContext';

function NotFoundView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, userInfo } = useAuth();

  const primary = appTheme.colors.events.primary;
  const primaryDark = appTheme.colors.events.primaryDark;
  const bg = appTheme.colors.events.secondary;

  const pathTried = location?.pathname || '/unknown';

  return (
    <Box bgcolor={bg} minHeight="100vh">
      <AppBarPadding />
      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center" justifyContent="center" sx={{ mt: { xs: 2, sm: 4 } }}>
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                borderRadius: 4,
                p: { xs: 2.5, sm: 3.5 },
                bgcolor: 'white',
                boxShadow: 3,
                textAlign: 'center',
              }}
            >
              <Typography variant={isMobile ? 'h4' : 'h3'} fontWeight={900} color={primaryDark} gutterBottom>
                404
              </Typography>

              <Typography variant="h6" fontWeight={700} color={primaryDark} gutterBottom>
                Page not found
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
                We couldn’t find <strong>{pathTried}</strong>. It may have been moved, deleted, or the URL might be mistyped.
              </Typography>

              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(-1)}
                >
                  Go back
                </Button>
              </Stack>

              <Divider sx={{ my: { xs: 2, sm: 3 } }} />

              <Typography variant="subtitle1" fontWeight={700} color={primaryDark} gutterBottom>
                Quick actions
              </Typography>

              <Grid container spacing={1.5} justifyContent="center">
                <Grid size={{ xs: 12, sm: 'auto' }}>
                  <Button
                    component={RouterLink}
                    to="/"
                    variant="contained"
                    color="inherit"
                    startIcon={<HomeIcon />}
                    sx={{
                      borderRadius: 3,
                      bgcolor: primary,
                      color: bg,
                      '&:hover': { bgcolor: primaryDark, color: 'white' },
                      minWidth: 180,
                    }}
                  >
                    Home
                  </Button>
                </Grid>

                <Grid size={{ xs: 12, sm: 'auto' }}>
                  <Button
                    component={RouterLink}
                    to="/_/contact"
                    variant="outlined"
                    startIcon={<ContactSupportIcon />}
                    sx={{ borderRadius: 3, minWidth: 180 }}
                  >
                    Contact support
                  </Button>
                </Grid>

                {isAuthenticated && (
                  <Grid size={{ xs: 12, sm: 'auto' }}>
                    <Button
                      component={RouterLink}
                      to="/_/storefront/manage"
                      variant="outlined"
                      startIcon={<StorefrontIcon />}
                      sx={{ borderRadius: 3, minWidth: 220 }}
                    >
                      Go to my Storefront
                    </Button>
                  </Grid>
                )}

                {isAuthenticated && userInfo?.is_organizer && (
                  <Grid size={{ xs: 12, sm: 'auto' }}>
                    <Button
                      component={RouterLink}
                      to="/_/events/manage"
                      variant="outlined"
                      startIcon={<EventAvailableIcon />}
                      sx={{ borderRadius: 3, minWidth: 240 }}
                    >
                      Manage my events
                    </Button>
                  </Grid>
                )}
              </Grid>

              <Stack spacing={0.5} sx={{ mt: { xs: 2, sm: 3 } }}>
                <Typography variant="caption" color="text.secondary">
                  Any previous changes you made were likely saved.
                </Typography>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <BottomPadding />
    </Box>
  );
}

export default NotFoundView;